<template>
  <div class="aso-upload-card" :class="{ 'aso-upload-card--disabled': disabled }">
    <p class="aso-upload-card__label">{{ label }}</p>

    <div class="aso-upload-card__trigger" ref="triggerRef">
      <button
        type="button"
        class="aso-upload-card__visual"
        :style="visualStyle"
        :disabled="disabled || busy"
        @click="handlePrimaryAction"
      >
        <span v-if="loading" class="aso-upload-card__loader">Falta pouco...</span>
        <span v-else-if="uploaded" class="aso-upload-card__status">Arquivo validado</span>
        <span v-else-if="hasError" class="aso-upload-card__status">Falha no upload</span>
        <span v-else class="aso-upload-card__status">Toque para selecionar</span>
      </button>

      <div v-if="showMobileOptions" class="aso-upload-card__tooltip">
        <button
          type="button"
          class="aso-upload-card__tooltip-btn aso-upload-card__tooltip-btn--primary"
          :disabled="disabled || busy"
          @click="openCamera"
        >
          Abrir camera
        </button>
        <button
          type="button"
          class="aso-upload-card__tooltip-btn"
          :disabled="disabled || busy"
          @click="openGallery"
        >
          Selecionar arquivo
        </button>
      </div>
    </div>

    <input
      ref="cameraInput"
      type="file"
      accept="image/png,image/jpeg"
      capture="environment"
      class="aso-upload-card__hidden-input"
      :disabled="disabled || busy"
      @change="onSelect"
    >

    <input
      ref="galleryInput"
      type="file"
      :accept="galleryAccept"
      class="aso-upload-card__hidden-input"
      :disabled="disabled || busy"
      @change="onSelect"
    >
  </div>
</template>

<script>
import docFrontUploader from 'assets/img/mockups/doc-front-uploader.png'
import docFrontUploaderSuccess from 'assets/img/mockups/doc-front-uploader-success.png'
import docFrontUploaderError from 'assets/img/mockups/doc-front-uploader-error.png'
import docBackUploader from 'assets/img/mockups/doc-back-uploader.png'
import docBackUploaderSuccess from 'assets/img/mockups/doc-back-uploader-success.png'
import docBackUploaderError from 'assets/img/mockups/doc-back-uploader-error.png'

export default {
  name: 'UploadDocumentCard',
  props: {
    side: {
      type: String,
      required: true,
    },
    label: {
      type: String,
      required: true,
    },
    busy: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    acceptPdf: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    hasError: {
      type: Boolean,
      default: false,
    },
    uploaded: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showMobileOptions: false,
    }
  },
  computed: {
    galleryAccept() {
      return this.acceptPdf ? 'image/png,image/jpeg,application/pdf' : 'image/png,image/jpeg'
    },
    isMobile() {
      return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    },
    visualImage() {
      const isFront = this.side === 'frente'

      if (this.hasError) {
        return isFront ? docFrontUploaderError : docBackUploaderError
      }

      if (this.uploaded) {
        return isFront ? docFrontUploaderSuccess : docBackUploaderSuccess
      }

      return isFront ? docFrontUploader : docBackUploader
    },
    visualStyle() {
      return {
        backgroundImage: 'url(' + this.visualImage + ')',
      }
    },
  },
  beforeDestroy() {
    document.removeEventListener('click', this.handleClickOutside, true)
  },
  methods: {
    handlePrimaryAction() {
      if (this.disabled || this.busy) {
        return
      }

      if (this.isMobile) {
        this.showMobileOptions = !this.showMobileOptions
        if (this.showMobileOptions) {
          document.addEventListener('click', this.handleClickOutside, true)
        } else {
          document.removeEventListener('click', this.handleClickOutside, true)
        }
        return
      }

      this.openGallery()
    },
    handleClickOutside(event) {
      if (!this.showMobileOptions) {
        return
      }

      const trigger = this.$refs.triggerRef
      if (trigger && !trigger.contains(event.target)) {
        this.showMobileOptions = false
        document.removeEventListener('click', this.handleClickOutside, true)
      }
    },
    openCamera() {
      if (this.disabled || this.busy) {
        return
      }

      this.showMobileOptions = false
      document.removeEventListener('click', this.handleClickOutside, true)
      this.$refs.cameraInput.click()
    },
    openGallery() {
      if (this.disabled || this.busy) {
        return
      }

      this.showMobileOptions = false
      document.removeEventListener('click', this.handleClickOutside, true)
      this.$refs.galleryInput.click()
    },
    onSelect(event) {
      const file = event && event.target && event.target.files && event.target.files[0]
      if (!file) {
        return
      }

      this.$emit('selected', {
        side: this.side,
        file,
      })

      event.target.value = ''
    },
  },
}
</script>

<style scoped>
.aso-upload-card {
  --branco: #ffffff;
  --background-50: #f8fafb;
  --brancos-100: #dddfe0;
  --azul-claro: #ecf5fc;
  --azul-50: #0575e6;
  --azul-100: #02298a;
  --azul-300: #002855;
  --cinza-100: #7d8597;
  --cinza-300: #33415c;
  --vermelho: #f04438;
  font-family: "Poppins", "Helvetica Neue", Arial, sans-serif;
  display: grid;
  gap: 8px;
}

.aso-upload-card--disabled {
  opacity: 0.65;
}

.aso-upload-card__label {
  margin: 0;
  font-weight: 600;
  color: var(--cinza-100);
  font-size: 12px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-align: center;
}

.aso-upload-card__visual {
  border: 1px solid var(--brancos-100);
  border-radius: 8px;
  height: 150px;
  width: 100%;
  background-size: 40%;
  background-repeat: no-repeat;
  background-position: center 42%;
  background-color: var(--background-50);
  display: grid;
  align-content: end;
  justify-items: center;
  cursor: pointer;
  padding-bottom: 10px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.aso-upload-card__visual:hover:not(:disabled) {
  border-color: #8ac0f2;
  box-shadow: 0 0 0 3px rgba(5, 117, 230, 0.12);
}

.aso-upload-card__visual:disabled {
  cursor: not-allowed;
}

.aso-upload-card__status {
  font-size: 12px;
  color: var(--cinza-300);
  background: rgba(255, 255, 255, 0.88);
  border-radius: 4px;
  padding: 2px 6px;
}

.aso-upload-card__loader {
  font-size: 12px;
  color: var(--azul-300);
  font-weight: 600;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 4px;
  padding: 2px 8px;
}

.aso-upload-card__trigger {
  position: relative;
}

.aso-upload-card__tooltip {
  position: absolute;
  top: calc(100% + 10px);
  right: 8px;
  z-index: 20;
  width: 220px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--brancos-100);
  border-radius: 10px;
  background-color: var(--branco);
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.18);
}

.aso-upload-card__tooltip::before {
  content: '';
  position: absolute;
  top: -6px;
  right: 16px;
  width: 10px;
  height: 10px;
  background-color: var(--branco);
  border-top: 1px solid var(--brancos-100);
  border-left: 1px solid var(--brancos-100);
  transform: rotate(45deg);
}

.aso-upload-card__tooltip-btn {
  width: 100%;
  padding: 9px 12px;
  font-size: 13px;
  font-weight: 600;
  border: 1px solid var(--brancos-100);
  border-radius: 6px;
  background-color: var(--branco);
  color: var(--cinza-300);
  transition: background 0.2s ease, border-color 0.2s ease;
}

.aso-upload-card__tooltip-btn:hover:enabled {
  border-color: #8ac0f2;
  background-color: #f3f9ff;
}

.aso-upload-card__tooltip-btn--primary {
  background-color: var(--azul-50);
  border-color: var(--azul-50);
  color: var(--branco);
}

.aso-upload-card__tooltip-btn--primary:hover:enabled {
  background-color: var(--azul-100);
  border-color: var(--azul-100);
}

.aso-upload-card__hidden-input {
  display: none;
}
</style>



// WEBPACK FOOTER //
// UploadDocumentCard.vue?fe941460